import { Component, ojs, app, v, state } from "modular-openscriptjs";
import { monetary, formatThousand, either } from "../../../helpers";

const h = app("h");
const gc = app("gc");

export default class ProductView extends Component {
    expanded = state(false);

    render(item, order, ...args) {
        const product = item.versioned_buyable;
        if (!product) return h.empty();

        const userCurrency = gc.user?.currency || "KES";
        const hasFeatures = item.features && item.features.length > 0;
        const hasExtras = item.extras && item.extras.length > 0;

        // Split args: first is status badge, second is action buttons, third is parent styling/attributes
        const statusBadge = args[0] || h.empty();
        const actionButtons = args[1] || h.empty();
        const cardAttributes = args[2] || {};

        // Merge default layout/flex classes with parent card attributes
        const defaultClasses = "position-relative d-flex flex-column gap-3";
        const mergedClass = `${cardAttributes.class || ""} ${defaultClasses}`.trim();
        const mergedAttributes = {
            ...cardAttributes,
            class: mergedClass
        };

        return h.div(
            mergedAttributes,
            // Status Badge at the top
            statusBadge,

            // First Container: Image on left, Name & Price on right
            h.div(
                { class: "d-flex align-items-center gap-3" },
                // Image or Placeholder
                either(
                    product.thumbnail || (product.images && product.images.length > 0),
                    () => h.img({
                        src: product.thumbnail || product.images[0]?.path,
                        class: "rounded-3 object-fit-cover border flex-shrink-0",
                        style: "width: 80px; height: 80px;"
                    }),
                    () => h.div(
                        {
                            class: "bg-light rounded-3 d-flex align-items-center justify-content-center text-secondary border border-light-subtle flex-shrink-0",
                            style: "width: 80px; height: 80px;"
                        },
                        h.i({ class: "bi bi-box fs-2" })
                    )
                ),

                // Name and Price
                h.div(
                    { class: "flex-fill min-w-0" },
                    h.div(
                        { class: "d-flex gap-2 flex-column" },
                        h.h4({ class: "fw-bold text-dark mb-0 text-truncate fs-4 text-wrap" }, product.name),
                        h.div(
                            { class: "flex-shrink-0" },
                            h.div({ class: "fw-bold text-primary fs-4" }, monetary(item.price * item.quantity, userCurrency)),
                            // A global/Carata-funded deal isn't shown here - the customer was
                            // still charged the normal price, and per business-side rules only
                            // the business's own deals are surfaced on this screen.
                            //
                            // item.price above already reflects this deal - for a
                            // business-funded deal it's reverse-bracketed from the
                            // discounted total (see OrderService::computeItemFinancials()),
                            // so the price shown above is already the reduced amount.
                            // item.discounted_price is a different, larger bracket (full
                            // retail price including Carata's markup) and isn't a further
                            // discount on top of item.price, so it isn't shown here as a
                            // comparison.
                            either(item?.deal && item.deal.company_id !== null && item?.discounted_price != null, () => h.div(
                                { class: "text-muted fs-xsm" },
                                h.i({ class: "bi bi-tag-fill me-1 text-success" }),
                                `Deal: ${item.deal.name} applied`
                            )),
                            // Packaging (container) fee is already folded into the total price
                            // above, and it's never commissioned - call it out separately so the
                            // business can see it clearly, same as the commission line below.
                            either(item?.package_price > 0, () => h.div(
                                { class: "text-muted fs-xsm" },
                                h.i({ class: "bi bi-box-seam me-1" }),
                                "Packaging Fee: ",
                                h.strong({ class: "text-dark" }, monetary(item.package_price * item.quantity, userCurrency)),
                                " (not commissioned)"
                            )),
                            either(item?.company_id, () => h.div(
                                { class: "text-muted fs-xsm" },
                                "Business Commission: ",
                                h.strong({ class: "text-dark" }, `${Math.ceil(item?.business_commission_rate ?? 0)}%`),
                                either(item?.business_commission > 0, () => h.span(
                                    ` (${monetary(item.business_commission, userCurrency)})`
                                ))
                            ))
                        )
                    )
                )
            ),

            // Second Container: Quantity and shortened Description with Read More (highly styled)
            h.div(
                { class: "d-flex flex-column gap-2 bg-light-subtle p-3 rounded-3 border border-light-subtle" },
                h.div({ class: "text-primary fw-bold fs-4 d-flex align-items-center gap-2" }, 
                    h.i({ class: "bi bi-cart-fill fs-4" }), 
                    `Quantity: ${item.quantity}`
                ),
                this.renderDescription(product.plain_description || product.short_description)
            ),

            // Features (without price) & Extras under "With" (styled prominently)
            either(hasFeatures || hasExtras, () => h.div(
                { class: "d-flex flex-column gap-3" },
                either(hasFeatures, () => h.div(
                    { class: "d-flex flex-wrap gap-2" },
                    item.features.map(f => h.span(
                        { class: "badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle rounded-3 fs-5 fw-semibold py-2.5 px-3.5" },
                        `${f.feature_name}: `,
                        h.strong({ class: "text-dark fw-bold" }, f.feature_value)
                    ))
                )),
                // either(hasExtras, () => h.div(
                //     { class: "fs-5 text-dark bg-success-subtle p-3 rounded-3 border border-success-subtle d-flex flex-column gap-2" },
                //     h.div({ class: "fw-bold text-success d-flex align-items-center gap-2 fs-5" }, 
                //         h.i({ class: "bi bi-plus-circle-fill fs-5" }), 
                //         "With:"
                //     ),
                //     h.div(
                //         { class: "d-flex flex-wrap gap-2" },
                //         item.extras.map(e => h.span(
                //             { class: "badge bg-white text-success-emphasis border border-success rounded-3 fs-5 fw-semibold py-2.5 px-3.5" },
                //             this.pluralizeExtra(e.qty, e.extra_name)
                //         ))
                //     )
                // ))
            )),

            // Action Buttons at the bottom
            actionButtons
        );
    }

    pluralizeExtra(qty, name) {
        if (qty <= 1) return `${qty} ${name}`;
        let pluralName = name;
        if (!name.endsWith('s')) {
            if (name.endsWith('y')) {
                pluralName = name.slice(0, -1) + 'ies';
            } else if (name.endsWith('ch') || name.endsWith('sh') || name.endsWith('x')) {
                pluralName = name + 'es';
            } else {
                pluralName = name + 's';
            }
        }
        return `${qty} ${pluralName}`;
    }

    renderDescription(desc) {
        if (!desc) return h.empty();
        
        const limit = 80;
        if (desc.length <= limit) {
            return h.div({ class: "text-muted fs-5" }, desc);
        }

        return v(this.expanded, (isExpanded) => {
            if (isExpanded.value) {
                return h.div(
                    { class: "text-muted fs-5" },
                    desc,
                    " ",
                    h.a({
                        href: "#",
                        class: "text-primary fw-bold text-decoration-none ms-1 fs-5",
                        listeners: {
                            click: (e) => {
                                e.preventDefault();
                                this.expanded.value = false;
                            }
                        }
                    }, "Read less")
                );
            } else {
                return h.div(
                    { class: "text-muted fs-5" },
                    desc.substring(0, limit) + "...",
                    " ",
                    h.a({
                        href: "#",
                        class: "text-primary fw-bold text-decoration-none ms-1 fs-5",
                        listeners: {
                            click: (e) => {
                                e.preventDefault();
                                this.expanded.value = true;
                            }
                        }
                    }, "Read more")
                );
            }
        });
    }
}

ojs(ProductView);
